import type { Candidate } from './types';

/**
 * Wonach die Prüflingsliste geordnet werden kann (Vorgabe des Nutzers, 2026-09-13).
 *
 * Die Reihenfolge hier ist zugleich die Reihenfolge der Knöpfe im Blatt.
 */
export const SORTIERARTEN = ['nachname', 'vorname', 'matrikel', 'pruefungsnr'] as const;

export type Sortierart = (typeof SORTIERARTEN)[number];

/**
 * Die Nummer, unter der ein Prüfling gezeigt wird: Prüfungsnummer, sonst Matrikelnummer.
 *
 * ⚠️ **Nie `id`.** Das ist Relays Kennung (eine UUID) und sagt dem Prüfer nichts — im
 * Wechseldialog stand sie bis zum 2026-10-02 im Titel. Gibt es keine Nummer, `null`.
 */
export const anzeigeNummer = (c: Candidate): string | null =>
  c.examNo || c.matriculationNumber || null;

/**
 * Eine Nummer zum Vergleichen — Zahl vor Text, Leeres zuletzt.
 *
 * ⚠️ **Drei Fälle, nicht zwei.** Eine Nummer kann eine Zahl sein („300"), etwas mit Buchstaben
 * („M-2") oder gar nichts. Zahlen müssen numerisch verglichen werden, sonst steht „1000" vor
 * „300". Buchstabige gehören in ihre eigene Ordnung und dürfen nicht mit den leeren ans Ende
 * fallen. Und Leeres gehört ans Ende: wer nach Prüfungsnummer sucht, will die Nummerierten
 * beieinander haben, nicht die Lücken zuerst.
 */
function nummernSchluessel(wert: string | null): [number, number, string] {
  const text = (wert ?? '').trim();
  if (text === '') return [2, 0, ''];
  const zahl = Number(text);
  if (Number.isFinite(zahl)) return [0, zahl, ''];
  return [1, 0, text.toLowerCase()];
}

const vergleicheNummer = (a: string | null, b: string | null): number => {
  const [ax, ay, az] = nummernSchluessel(a);
  const [bx, by, bz] = nummernSchluessel(b);
  return ax - bx || ay - by || az.localeCompare(bz);
};

/** Der Rückfall bei Gleichstand — ohne ihn springt die Reihenfolge bei jedem Neuzeichnen. */
const nachNamen = (a: Candidate, b: Candidate): number =>
  a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName);

/**
 * Die Prüflingsliste ordnen. Reine Rechnung, die übergebene Liste bleibt unangetastet.
 */
export function sortiereKandidaten(liste: Candidate[], art: Sortierart): Candidate[] {
  const kopie = [...liste];
  switch (art) {
    case 'vorname':
      return kopie.sort((a, b) => a.firstName.localeCompare(b.firstName) || nachNamen(a, b));
    case 'matrikel':
      return kopie.sort((a, b) => vergleicheNummer(a.matriculationNumber, b.matriculationNumber) || nachNamen(a, b));
    case 'pruefungsnr':
      return kopie.sort((a, b) => vergleicheNummer(a.examNo, b.examNo) || nachNamen(a, b));
    default:
      return kopie.sort(nachNamen);
  }
}
